import {
  Checkbox,
  PriceField,
  RadioButtonGroup,
  TextOverflow,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import {
  GreyGrey10,
  GreyGrey70,
  Primary13Regular,
  Primary13SemiBold,
  RedRed60
} from '@sequencehq/core-components'
import { MinimumScopeTarget } from 'modules/Cube/view/common/drawers/minimumEditor/drawer/minimumEditor.types'
import { useMinimumEditorDomainContext } from '../../minimumEditor.domain.context'
import { toBillingFrequencyLabel, toPriceSummary } from '@sequencehq/utils'

const useMinimumEditorForm = () => {
  const minimumEditorContext = useMinimumEditorDomainContext()

  return {
    fieldConfig: minimumEditorContext.queries.form.fields,
    availablePrices: minimumEditorContext.queries.editor.availablePrices,
    currency: minimumEditorContext.queries.editor.currency
  }
}

const FIELD_STYLES = {
  label: {
    ...Primary13SemiBold,
    color: GreyGrey70
  }
}

export const MinimumEditorForm = () => {
  const formHook = useMinimumEditorForm()

  return (
    <Flex flexDirection="column" gap="16px" data-testid="minimumEditor.form">
      <PriceField
        label="Minimum amount"
        {...formHook.fieldConfig.value}
        isDisabled={formHook.fieldConfig.value.disabled}
        currency={formHook.currency}
        data-testid="minimumEditor.amount"
        variant="v2"
        styles={FIELD_STYLES}
      />
      <Box>
        {/** The applyTo field - at some point we may want to extract this input style and create an input from it! */}
        <Box {...FIELD_STYLES.label} marginBottom="8px">
          Apply to
        </Box>
        <Box
          marginBottom="16px"
          borderRadius="6px"
          border={
            formHook.fieldConfig['scope.priceIds'].validationErrors.length
              ? `1px solid ${RedRed60}`
              : undefined
          }
          overflow={'hidden'}
        >
          <Box width="100%">
            <RadioButtonGroup
              fullWidth
              {...formHook.fieldConfig['scope.target']}
              isDisabled={formHook.fieldConfig['scope.target'].disabled}
              onChange={newValue =>
                formHook.fieldConfig['scope.target'].onChange(
                  newValue as MinimumScopeTarget
                )
              }
              data-testid="minimumEditor.scope"
            />
          </Box>
          {!formHook.fieldConfig['scope.priceIds'].hidden && (
            <Box>
              {formHook.fieldConfig['scope.priceIds'].options.map(option => {
                const price = formHook.availablePrices.find(
                  p => p.id === option.value
                )
                if (!price) return null

                const selected = formHook.fieldConfig[
                  'scope.priceIds'
                ].value?.includes(option.value)
                const disabled =
                  option.disabled ||
                  formHook.fieldConfig['scope.target'].disabled
                return (
                  <Grid
                    data-testid={`minimum.scope.price.${option.value}`}
                    key={option.value}
                    alignItems="center"
                    padding="8px"
                    templateColumns="auto 3fr 2fr"
                    cursor="pointer"
                    {...Primary13Regular}
                    _hover={{
                      backgroundColor: GreyGrey10
                    }}
                    {...(disabled
                      ? {
                          opacity: 0.4
                        }
                      : {})}
                    onClick={() => {
                      if (disabled) return

                      formHook.fieldConfig['scope.priceIds'].onChange(
                        selected
                          ? formHook.fieldConfig[
                              'scope.priceIds'
                            ].value?.filter(v => v !== option.value)
                          : [
                              ...(formHook.fieldConfig['scope.priceIds']
                                .value || []),
                              option.value
                            ]
                      )
                    }}
                  >
                    <Box paddingRight="8px">
                      <Checkbox isChecked={selected} disabled={disabled} />
                    </Box>
                    <Box paddingRight="8px" overflow={'hidden'}>
                      <TextOverflow {...Primary13Regular} splitPercentage={100}>
                        {price.name}
                      </TextOverflow>
                    </Box>
                    <Box overflow={'hidden'}>
                      <TextOverflow
                        {...Primary13Regular}
                        splitPercentage={100}
                        textAlign="right"
                      >
                        {`${toPriceSummary(
                          price
                        )} / ${toBillingFrequencyLabel(price.billingFrequency)}`}
                      </TextOverflow>
                    </Box>
                  </Grid>
                )
              })}
            </Box>
          )}
        </Box>
        {Boolean(
          formHook.fieldConfig['scope.priceIds'].validationErrors.length
        ) && (
          <Box
            marginTop="-8px"
            marginBottom="16px"
            key={
              formHook.fieldConfig['scope.priceIds'].validationErrors[0].message
            }
            {...Primary13Regular}
            color={RedRed60}
          >
            {formHook.fieldConfig['scope.priceIds'].validationErrors[0].message}
          </Box>
        )}
      </Box>
    </Flex>
  )
}
